<script setup lang="ts">
import { Menu } from 'lucide-vue-next'

const { ecommerce } = useAnalytics()
const router = useRouter()
const route = useRoute()
const { el: headerRef, isHeaderVisible } = useUiHeader()
const isCheckoutPage = computed(() => ((route?.name as string) ?? '').includes('checkout'))
const { t } = useI18n()
const localePath = useLocalePath()
const { navigation: accountNav } = useAccount()
const collectionsStore = useCollectionsStore()
const uiStore = useUiStore()

const loggedIn = computed(() => useAuth().loggedIn.value === true)

const desktopSearchRef = useTemplateRef('desktopSearchRef')
const mobileSearchRef = useTemplateRef('mobileSearchRef')

const openDropdownMenu = ref(false)
function onDropdownMenuOpen(value: boolean) {
  if (!loggedIn.value) {
    return router.push(localePath('/account'))
  }
  openDropdownMenu.value = value
}

async function handleCartClick() {
  uiStore.openCartDrawer()
  ecommerce.viewCart()
}

function handleMobileSearchClick() {
  mobileSearchRef.value?.handleOpen()
}

function handleMenuClick() {
  nextTick(() => {
    uiStore.isNavigationDrawerOpen = true
  })
}

const activeOrderStore = useActiveOrderStore()
const totalQuantity = computed(() => activeOrderStore.activeOrder.data?.totalQuantity ?? 0)
</script>

<template>
  <div
    ref="headerRef"
    :class="
      isHeaderVisible ? 'translate-y-0' : '-translate-y-full md:translate-y-0'
    "
    class="sticky top-0 z-50 w-full flex-none transform border-primary/[0.06] bg-background/95 backdrop-blur transition-colors transition-transform duration-500 ease-in-out lg:border-b lg:border-primary/10 dark:bg-transparent"
  >
    <div
      class="mx-auto max-w-3xl border-b border-primary/10 px0 lg:max-w-[88rem] lg:border-0 lg:px6 lg:divide-y lg:divide-border md:max-w-4xl sm:px2 sm:px4"
    >
      <div class="mx-1 h-16 flex justify-between py-4 lg:mx-0 md:h-20">
        <!-- Logo -->
        <div class="flex flex-1 items-center px-2 lg:px-0">
          <div class="flex shrink-0 items-center">
            <div v-if="!isCheckoutPage" class="block lg:hidden">
              <UiButton
                id="mobile-navigation"
                :aria-label="t('general.navigation.label')"
                variant="ghost"
                size="icon"
                class="relative z-100 text-primary"
                data-testid="mobile-navigation-button"
                @click="handleMenuClick"
                @touchstart.passive="handleMenuClick"
              >
                <Menu class="size-5!" />
              </UiButton>
            </div>

            <div
              class="absolute left-1/2 my-auto md:relative md:left-auto md:ml3 md:translate-x-0 lg:ml0 -translate-x-1/2"
            >
              <NuxtLinkLocale
                to="/"
                class="mr3 w18 flex overflow-hidden md:w-auto xs:w-[5.75rem]"
                :class="{ 'md:mr3 mr0': isCheckoutPage }"
                :aria-label="t('general.homepage.label')"
              >
                <UzoLogo class="h12 w-auto text-primary md:rd-sm md:bg-primary md:px6 md:py2 md:text-white" />
              </NuxtLinkLocale>
            </div>

            <div v-if="!isCheckoutPage" class="mx2 hidden lg:inline-flex">
              <div
                class="h-full flex grow-1 flex-row items-center justify-start"
              >
                <NuxtLinkLocale
                  to="/collections/best-sellers"
                  class="relative mx0 h10 w-auto flex shrink-0 cursor-pointer items-center justify-center ws-nowrap text-error fw600 after:absolute after:inset-0 after:keyframes-fade-out-scale-down after:animate-[fade-out-scale-down_.2s_ease_both] after:rounded-full after:bg-accent after:will-change-transform after:content-[''] after:-z-1 hover:after:keyframes-fade-in-scale-up hover:after:animate-[fade-in-scale-up_.24s_linear_both]"
                >
                  <Icon name="i-mdi:star-circle" class="mr1" />
                  <span :title="t('pages.title.best_sellers')">
                    {{ t("pages.title.best_sellers") }}
                  </span>
                </NuxtLinkLocale>
              </div>

              <!-- <div
                class="h-full flex grow-1 flex-row items-center justify-start"
              >
                <NuxtLinkLocale
                  to="/collections/top-for-less"
                  class="relative mx0 h10 w-auto flex shrink-0 cursor-pointer items-center justify-center ws-nowrap rounded-full px2.5 text-primary fw600 after:absolute after:inset-0 after:keyframes-fade-out-scale-down after:animate-[fade-out-scale-down_.2s_ease_both] after:rounded-full after:bg-accent after:will-change-transform after:content-[''] after:-z-1 hover:after:keyframes-fade-in-scale-up hover:after:animate-[fade-in-scale-up_.24s_linear_both]"
                >
                  <span :title="t('page.top_for_less.special_offer')">{{
                    t("page.top_for_less.special_offer")
                  }}</span>
                </NuxtLinkLocale>
              </div> -->
            </div>
          </div>

          <div v-if="!isCheckoutPage" class="ml4 max-w-lg w-full hidden md:block">
            <SearchStore ref="desktopSearchRef" variant="inline" />
          </div>

          <div
            class="flex flex-1 items-center justify-end"
            :class="{ hidden: isCheckoutPage }"
          >
            <div v-if="!isCheckoutPage" class="hidden sm:block">
              <LanguageButton />
            </div>

            <!-- Hidden SearchStore for mobile -->
            <SearchStore ref="mobileSearchRef" variant="overlay" hide-input class="hidden" />

            <UiButton
              :aria-label="t('general.search.label')"
              size="icon"
              variant="ghost"
              class="relative z-[100] text-primary md:hidden"
              @click="handleMobileSearchClick"
            >
              <Icon name="lucide:search" class="text-xl text-primary" />
            </UiButton>

            <UiButton
              v-if="!isCheckoutPage"
              id="account"
              :aria-label="t('page.account.title')"
              size="icon"
              variant="ghost"
              class="text-primary hidden"
              as-child
            >
              <NuxtLinkLocale to="/account">
                <Icon name="lucide:user-round" class="text-xl" />
              </NuxtLinkLocale>
            </UiButton>

            <UiDropdownMenu
              v-if="!isCheckoutPage"
              :open="openDropdownMenu"
              @update:open="onDropdownMenuOpen"
            >
              <UiDropdownMenuTrigger as-child>
                <UiButton
                  id="userAccount"
                  data-testid="userAccount"
                  :aria-label="t('general.navigation.label')"
                  size="icon"
                  variant="ghost"
                  class="text-primary hidden sm:inline-flex"
                >
                  <Icon name="lucide:user-round" class="text-xl" />
                </UiButton>
              </UiDropdownMenuTrigger>
              <UiDropdownMenuContent>
                <UiDropdownMenuItem
                  v-for="accountNavItem in accountNav"
                  :key="accountNavItem.label"
                  as-child
                >
                  <NuxtLinkLocale :to="accountNavItem.to">
                    <Icon
                      v-if="accountNavItem.icon"
                      :name="accountNavItem.icon"
                      class="mr2"
                    />
                    {{ accountNavItem.label }}
                  </NuxtLinkLocale>
                </UiDropdownMenuItem>
                <UiDropdownMenuSeparator />
                <UiDropdownMenuItem @click="authLogout">
                  {{ t("form.sign_out.label") }}
                </UiDropdownMenuItem>
              </UiDropdownMenuContent>
            </UiDropdownMenu>

            <div class="relative flex items-center text-primary">
              <UiButton
                id="cart"
                data-testid="cart"
                :aria-label="t('cart.label')"
                size="icon"
                variant="ghost"
                @click="handleCartClick"
              >
                <Icon name="lucide:shopping-cart" class="text-xl" />
              </UiButton>

              <ClientOnly>
                <span
                  v-if="totalQuantity > 0"
                  class="pointer-events-none absolute h6 w6 flex items-center justify-center rounded-full bg-error text-sm text-white -right-2 -top-1"
                >
                  {{ totalQuantity }}
                </span>
              </ClientOnly>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!isCheckoutPage"
        class="h-14 overflow-hidden border-b border-primary/10 hidden lg:block"
      >
        <div class="flex content-center items-center lg:flex lg:py-2 lg:space-x-8">
          <UiButton
            id="desktop-navigation"
            :aria-label="t('general.navigation.label')"
            variant="ghost"
            size="icon"
            class="relative z-100 text-primary"
            data-testid="desktop-navigation-button"
            @click="handleMenuClick"
            @touchstart.passive="handleMenuClick"
          >
            <Menu class="size-5!" />
          </UiButton>

          <template
            v-if="collectionsStore.rootCollections.status === 'pending'"
          >
            <NSkeleton
              v-for="i in 9"
              :key="i"
              :style="{ width: `${100 + ((i * i + i) % 7) * 20}px` }"
              pulse
            />
          </template>
          <template
            v-else-if="
              collectionsStore.rootCollections.data
                && collectionsStore.rootCollections.data?.length
            "
          >
            <NuxtLinkLocale
              v-for="collection in collectionsStore.rootCollections.data.slice(
                0,
                9,
              )"
              :key="collection.id"
              :to="`/collections/${collection.id}/${collection.slug}`"
              class="ws-nowrap text-sm text-primary fw600 n-transition"
            >
              {{ collection.name }}
            </NuxtLinkLocale>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
